import { PureComponent, ReactNode } from 'react';
import getLabTestSlots, { Slot } from '../../../../api/getLabTestSlot';
import { DatePicker, Modal, Radio, RadioChangeEvent, notification } from 'antd';
import moment, { Moment } from 'moment';
import {
    AddressFormInput,
    CloseBtIcon,
    GetLabOrderStatus,
    getTimeIn12HourFormat,
    SelectedSlotDetails,
    showDateInText,
} from './BookLabTestsUtils';
import { track } from '../../../../tracking';
import { Order } from '../../../../api/fetchAllHEOrder';
import { OrderPlacedFromObj } from './OrderMedicineUtils';
import { Data as LabData } from '../../../../api/getLabTestsOrder';
import { GetCurrentOrderStatus } from '../../../../pages/HEDashboardUtils';
import { AntTooltip } from '../../../common/AntTooltip';
import rescheduleLabTestSlot from '../../../../api/rescheduleLabTestSlot';
import { getMySingleDoctorId } from '../../../../store/idbHandler';

type SlotDetails = { [k: string]: string };

type State = {
    slotOptions: Slot[];
    selectedOption: number;
    selectedDate: string;
    fetchingSlots?: boolean;
    isConfirmingNewSlot?: boolean;
    selectedSlotDetails?: SelectedSlotDetails;
};

type Props = {
    closeMe: () => void;
    vendor: string;
    vendor_id: string;
    selectSlotHandler?: (selectedSlotDetails: SelectedSlotDetails) => void;
    addressFormInput: AddressFormInput;
    selectedSlotDetails?: SelectedSlotDetails;
    order?: Order;
    onRefresh?: () => void;
    patientName?: string;
};

class SelectSlotModal extends PureComponent<Props, State> {
    state: State = {
        slotOptions: [],
        selectedOption: 0,
        selectedDate: moment().format('YYYY-MM-DD'),
    };
    dateOptions = new Array(3)
        .fill(0)
        .map((_, index) => moment().add(index, 'days').format('YYYY-MM-DD'));

    componentDidMount = async (): Promise<void> => {
        const { selectedSlotDetails } = this.props;

        if (!selectedSlotDetails) return this.getLabTestSlots(this.dateOptions[0]);
        const { collection_date } = selectedSlotDetails;
        this.setState({
            selectedDate: collection_date,
        });
        this.getLabTestSlots(collection_date);
    };

    getLabTestSlots = (date: string): void => {
        const { addressFormInput: address, vendor, vendor_id } = this.props;
        const docId = getMySingleDoctorId() || '';

        this.setState({ fetchingSlots: true, slotOptions: [], selectedOption: 0 });

        getLabTestSlots(docId, {
            vendor,
            vendor_id,
            date,
            address,
        })
            .then((i) => {
                if (i.success) {
                    const { data } = i;
                    const { selectedSlotDetails } = this.props;
                    let index = 0;
                    if (selectedSlotDetails) {
                        index = data.findIndex(
                            (slot) => slot.id === selectedSlotDetails.collection_slot.id,
                        );
                    }
                    this.setState({
                        slotOptions: data,
                        selectedOption: index,
                    });
                } else {
                    if (i.error) {
                        notification.error({
                            message: i.error,
                            duration: 5,
                        });
                        track('ERROR', { on: 'getLabTestSlots', what: i.error });
                    }
                    console.log(i.error);
                }
            })
            .catch((e) => {
                console.log(e);
                notification.error({
                    message: 'Get slots => ' + e,
                    duration: 5,
                });
            })
            .finally(() => {
                this.setState({ fetchingSlots: false });
            });
    };

    saveSlotHandler = (): void => {
        const { selectSlotHandler } = this.props;
        const { selectedDate, selectedOption, slotOptions } = this.state;
        const { id, details } = slotOptions[selectedOption];
        if (selectSlotHandler) {
            selectSlotHandler({
                collection_date: selectedDate,
                collection_slot: {
                    id,
                    details,
                },
            });
        }
    };

    onDateChange = (date: Moment | null): void => {
        const selectedDate = date?.format('YYYY-MM-DD');
        if (!selectedDate) return;
        this.setState({
            selectedDate,
        });
        this.getLabTestSlots(selectedDate);
    };

    disabledDate = (current: Moment | null): boolean => {
        const minDate = moment().startOf('day');
        const maxDate = moment().add(3, 'months');
        return current ? current < minDate || current > maxDate : false;
    };

    getValueFromOrder = (): {
        service_id: string;
        patient_name: string;
        gen: string | undefined;
        age: number;
        paidAmount: number | undefined;
        orderName: string;
    } => {
        const { order } = this.props;
        if (!order) {
            return {
                service_id: '',
                patient_name: 'Not available',
                gen: '',
                age: 0,
                paidAmount: 0,
                orderName: '',
            };
        }
        const { service_id, patient_name, customer_details, paidAmount } = order;
        const gen = customer_details?.gender?.[0]?.toUpperCase();
        const age = customer_details?.age || 0;
        const orderName = order?.orderName || '';

        return {
            service_id,
            patient_name: patient_name || 'Not available',
            gen,
            age,
            paidAmount,
            orderName,
        };
    };

    confirmNewSlotHandler = (): void => {
        const { order } = this.props;
        const { selectedDate, selectedOption, slotOptions } = this.state;
        const docId = getMySingleDoctorId() || '';
        const { id, details } = slotOptions[selectedOption];
        const order_id = order?.order_id || '';
        const requestPayload = {
            order_id,
            collection_date: selectedDate,
            collection_slot: {
                id,
                details,
            },
        };
        this.setState({
            isConfirmingNewSlot: true,
        });
        rescheduleLabTestSlot(docId, requestPayload)
            .then((i) => {
                if (i.success) {
                    track('RESCHEDULE_LAB_TEST_SLOT_SUCCESS', { order_id });
                    this.setState({
                        selectedSlotDetails: {
                            collection_date: selectedDate,
                            collection_slot: {
                                id,
                                details,
                            },
                        },
                    });
                } else {
                    notification.error({
                        message: i.error,
                        duration: 5,
                    });
                    track('ERROR', { on: 'rescheduleLabTestSlot', what: i.error });
                }
            })
            .catch((e) => {
                notification.error({
                    message: 'Reschedule lab test slot => ' + e,
                    duration: 5,
                });
                track('ERROR', { on: 'rescheduleLabTestSlot', what: (e as Error).message });
            })
            .finally(() => {
                this.setState({
                    isConfirmingNewSlot: false,
                });
            });
    };

    closeMe = (): void => {
        const { closeMe, onRefresh } = this.props;
        const { isConfirmingNewSlot, selectedSlotDetails } = this.state;
        if (isConfirmingNewSlot) return;
        if (selectedSlotDetails) {
            onRefresh && onRefresh();
        }
        closeMe();
    };

    getSlotDetails = (
        selectedSlotDetails: SelectedSlotDetails,
        slotDetails: SlotDetails,
    ): SlotDetails => {
        const { collection_date, collection_slot } = selectedSlotDetails;
        const {
            details: { start_time, end_time },
        } = collection_slot;
        slotDetails['date'] = collection_date;
        slotDetails['start_time'] = getTimeIn12HourFormat(start_time);
        slotDetails['end_time'] = getTimeIn12HourFormat(end_time);
        return slotDetails;
    };

    render(): ReactNode {
        const {
            order,
            vendor,
            patientName,
            selectedSlotDetails: propSelectedSlotDetails,
        } = this.props;
        const {
            slotOptions,
            selectedOption,
            selectedDate,
            fetchingSlots,
            isConfirmingNewSlot,
            selectedSlotDetails,
        } = this.state;
        const {
            saveSlotHandler,
            dateOptions,
            onDateChange,
            disabledDate,
            getValueFromOrder,
            confirmNewSlotHandler,
            closeMe,
            getSlotDetails,
        } = this;
        const isForRescheduleSlot = Boolean(order);
        const { service_id, patient_name, gen, age, paidAmount, orderName } = getValueFromOrder();
        const { text, icon } = OrderPlacedFromObj[service_id] || {};
        const disabled =
            slotOptions.length === 0 || !slotOptions[selectedOption] || isConfirmingNewSlot;
        let currnetSlotDetails: { [k: string]: string } = {};
        if (propSelectedSlotDetails) {
            currnetSlotDetails = getSlotDetails(propSelectedSlotDetails, currnetSlotDetails);
        }
        let slotDetails: { [k: string]: string } = {};
        if (selectedSlotDetails) {
            slotDetails = getSlotDetails(selectedSlotDetails, slotDetails);
        }

        return (
            <Modal
                bodyStyle={{
                    padding: '0px',
                    borderRadius: '8px !important',
                    height: '550px',
                }}
                visible={true}
                width={636}
                onCancel={closeMe}
                footer={null}
                closable={false}
                destroyOnClose
                style={{ top: 50, borderRadius: '8px !important', height: '550px' }}
                className="overflow-hidden rounded-8"
            >
                <div
                    className="flex flex-col h-full w-full rounded-8 bg-darwin-neutrals-50"
                    style={{ color: '#1A1A1A' }}
                >
                    <div className="px-16 py-12 flex items-center justify-between bg-white">
                        <p className="text-20">
                            {isForRescheduleSlot ? 'Reschedule Slot' : 'Select Slot'}
                        </p>
                        <CloseBtIcon closeMe={closeMe} />
                    </div>

                    <div className="relative flex-1 flex flex-col overflow-auto border-t border-b border-darwin-neutral_new-200 p-16">
                        {selectedSlotDetails ? (
                            <div className="flex-1 flex flex-col items-center justify-center space-y-16 bg-white rounded-8">
                                <p>
                                    <svg
                                        className="w-28 h-24 fill-darwin-green-primary"
                                        viewBox="0 0 27 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M13.5 24C9.1875 24 5.25 21.75 3.09375 18C0.9375 14.2969 0.9375 9.75 3.09375 6C5.25 2.29688 9.1875 0 13.5 0C17.7656 0 21.7031 2.29688 23.8594 6C26.0156 9.75 26.0156 14.2969 23.8594 18C21.7031 21.75 17.7656 24 13.5 24ZM18.7969 9.79688H18.75C19.2188 9.375 19.2188 8.67188 18.75 8.20312C18.3281 7.78125 17.625 7.78125 17.2031 8.20312L12 13.4531L9.79688 11.25C9.32812 10.7812 8.625 10.7812 8.20312 11.25C7.73438 11.6719 7.73438 12.375 8.20312 12.7969L11.2031 15.7969C11.625 16.2656 12.3281 16.2656 12.7969 15.7969L18.7969 9.79688Z" />
                                    </svg>
                                </p>
                                <p>
                                    The slot reschedule request to {vendor} has been successful for{' '}
                                    {patientName}!
                                </p>
                                <div className="flex flex-col items-center space-y-8 text-darwin-neutral_new-800">
                                    <p className="flex items-center space-x-5 font-700">
                                        <p>New slot request:</p>
                                        <p>{slotDetails['start_time']}</p>
                                        <p>-</p>
                                        <p>{slotDetails['end_time']}</p>
                                        <p>({showDateInText(slotDetails['date'])})</p>
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col">
                                <div className="sticky top-0 bg-darwin-neutrals-50 z-1">
                                    {order && (
                                        <div className="px-16 py-12 bg-white space-y-8 rounded-8 mb-24">
                                            <div className="flex items-center justify-between space-x-8">
                                                <div className="flex items-center space-x-8">
                                                    <p>
                                                        <svg
                                                            className="w-14 h-14 fill-darwin-neutrals-600"
                                                            viewBox="0 0 14 14"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path d="M9.18359 0.878906L9.99609 1.69141L12.4336 4.12891L13.2461 4.94141C13.5762 5.24609 13.5762 5.7793 13.2461 6.08398C12.9414 6.41406 12.4082 6.41406 12.1035 6.08398L11.875 5.85547L4.79102 12.9141C4.33398 13.3711 3.72461 13.625 3.06445 13.625H2.9375C1.5918 13.625 0.5 12.5332 0.5 11.1875V11.0605C0.5 10.4004 0.753906 9.79102 1.21094 9.33398L8.26953 2.27539L8.04102 2.02148C7.71094 1.7168 7.71094 1.18359 8.04102 0.878906C8.3457 0.548828 8.87891 0.548828 9.18359 0.878906ZM5.70508 7.125H8.26953L10.707 4.6875L9.4375 3.41797L5.70508 7.125Z" />
                                                        </svg>
                                                    </p>
                                                    <p className="text-13">
                                                        <span className="font-700">
                                                            {patient_name}
                                                        </span>
                                                        <span className="break-words">
                                                            {gen ? ` | ${gen}` : ''}
                                                            {age ? ` | ${age}y` : ''}
                                                        </span>
                                                    </p>
                                                </div>
                                                <div className="whitespace-nowrap flex items-center justify-end space-x-8">
                                                    <GetLabOrderStatus
                                                        order_details={
                                                            (order as unknown) as LabData
                                                        }
                                                        showTag
                                                    />
                                                    <GetCurrentOrderStatus order_details={order} />
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between space-x-8">
                                                <p className="text-13 truncate-overflow-1">
                                                    ₹ {paidAmount} | {orderName}
                                                </p>
                                                <p className="flex items-center justify-end whitespace-nowrap text-10 space-x-4 text-darwin-neutrals-600 uppercase font-700">
                                                    <span>Last updated at</span>
                                                    <span>
                                                        {moment(order.updated_at).format(
                                                            "DD MMM'YY, hh:mm A",
                                                        )}
                                                    </span>
                                                    <AntTooltip placement="bottom" content={text}>
                                                        <span>{icon}</span>
                                                    </AntTooltip>
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {isForRescheduleSlot && (
                                        <p className="flex items-center space-x-5 pl-2 mb-8">
                                            <p className="text-16 font-700">Select new slot:</p>
                                            <p>(Current slot details:</p>
                                            <p>{currnetSlotDetails['start_time']}</p>
                                            <p>-</p>
                                            <p>{currnetSlotDetails['end_time']}</p>
                                            <p>({showDateInText(currnetSlotDetails['date'])}))</p>
                                        </p>
                                    )}
                                </div>
                                <div className="flex-1 flex flex-col p-16 bg-white space-y-16 rounded-8">
                                    <Radio.Group
                                        value={selectedDate}
                                        onChange={(e: RadioChangeEvent) => {
                                            this.setState({
                                                selectedDate: e.target.value,
                                            });
                                            this.getLabTestSlots(e.target.value);
                                        }}
                                        disabled={fetchingSlots}
                                    >
                                        {dateOptions.map((i) => (
                                            <Radio value={i}>{showDateInText(i)}</Radio>
                                        ))}
                                        <DatePicker
                                            allowClear={false}
                                            value={moment(selectedDate)}
                                            format={'DD/MM/YYYY'}
                                            onChange={onDateChange}
                                            disabled={fetchingSlots}
                                            disabledDate={disabledDate}
                                        />
                                    </Radio.Group>
                                    <p className="text-11 font-500 uppercase">
                                        Slots for {showDateInText(selectedDate)}
                                    </p>
                                    {fetchingSlots ? (
                                        <div className="flex-1 flex items-center justify-center">
                                            <svg
                                                className="w-24 h-24 fill-darwin-accent-bright-blue-800 animate-spin"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 512 512"
                                            >
                                                <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                            </svg>
                                        </div>
                                    ) : slotOptions.length !== 0 ? (
                                        <div className="grid grid-cols-3 gap-12 overflow-scroll hide-scrollbar">
                                            {slotOptions.map(
                                                ({ details: { start_time, end_time } }, index) => (
                                                    <button
                                                        className={`text-13 text-ceter p-8 rounded-8 border ${
                                                            selectedOption === index
                                                                ? 'border-darwin-accent-bright-blue-800 bg-darwin-accent-bright-blue-100 font-700 text-darwin-accent-bright-blue-800'
                                                                : 'border-darwin-neutral_new-200'
                                                        }`}
                                                        onClick={() => {
                                                            this.setState({
                                                                selectedOption: index,
                                                            });
                                                        }}
                                                    >
                                                        {getTimeIn12HourFormat(start_time)} -{' '}
                                                        {getTimeIn12HourFormat(end_time)}
                                                    </button>
                                                ),
                                            )}
                                        </div>
                                    ) : (
                                        <p className="flex-1 flex items-center justify-center text-15 font-700">
                                            No Slots Available
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {isConfirmingNewSlot && (
                            <div className="absolute inset-0 z-50 flex items-center justify-center opacity-50 bg-white">
                                <svg
                                    className="w-28 h-28 fill-darwin-accent-bright-blue-800 animate-spin"
                                    role="img"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                >
                                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                </svg>
                            </div>
                        )}
                    </div>

                    <div className="px-16 py-12 flex items-center justify-between bg-white">
                        <button
                            className="px-11 py-9 border border-darwin-accent-bright-blue-800 bg-white text-darwin-accent-bright-blue-800 rounded-8 text-13 font-700 disabled:opacity-70"
                            onClick={closeMe}
                            disabled={isConfirmingNewSlot}
                        >
                            Cancel
                        </button>
                        {selectedSlotDetails ? (
                            <button
                                className="px-11 py-9 border border-darwin-accent-beight-blue-800 bg-darwin-accent-bright-blue-800 text-white rounded-8 text-13 font-700"
                                onClick={closeMe}
                            >
                                Got it
                            </button>
                        ) : (
                            <button
                                className="px-11 py-9 border border-darwin-accent-beight-blue-800 bg-darwin-accent-bright-blue-800 text-white rounded-8 text-13 font-700 disabled:opacity-70"
                                onClick={
                                    isForRescheduleSlot ? confirmNewSlotHandler : saveSlotHandler
                                }
                                disabled={disabled}
                            >
                                {isForRescheduleSlot ? 'Confirm New Slot' : 'Confirm Slot'}
                            </button>
                        )}
                    </div>
                </div>
            </Modal>
        );
    }
}

export default SelectSlotModal;
